/**
 * Echo Hunt -- the lucid interlude (P11, §8.4).
 *
 * When the suspicion meter boils over, §8.4 wants "the character notices something 'off, yet familiar',
 * checks, and becomes lucid. This is narrated in an interlude outside the hunt." This module produces
 * that passage: one call, once per story, then the story ends.
 *
 * PURE module: no React, no `store`, no DB imports. The backend is a parameter (so tests use
 * `MockLLMBackend`), exactly like every other step.
 *
 * ## Failure handling
 *
 * A failed interlude does NOT keep the story alive. The meter crossing is the fact; the passage is the
 * telling of it. If the model cannot write it, the story still ends -- with a written fallback passage
 * that says the same thing plainly -- because leaving the story running would silently ignore the
 * threshold, and the user's next round would be a continuation of a run that was supposed to be over.
 * That is the opposite of `WeaveStory`, which fails the round rather than degrade: here there is nothing
 * to degrade into, since the interlude is a record of something that has already happened.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.2, §8.4, §16.0, §16.2 P11.
 */
import {CallLLM, LLMBackend, LLMCallCounter} from "./LLM.js";
import {CheckBullet, CheckBulletOptions} from "./Normalize.js";
import type {EchoCharacter, EchoWorld} from "./Types.js";
import {
	Interlude_JSONSchema,
	Interlude_SystemPrompt,
	Interlude_UserPrompt,
	Interlude_UserPromptOptions,
} from "./Prompts/Interlude.js";

export type InterludeResult = {
	/** always non-empty: the model's passage, or the fallback when the model failed */
	text: string;
	/** true when the model produced `text`; false means the fallback stands in */
	generatedByModel: boolean;
	outputTokens: number;
	callCount: number;
	/** the raw reply, for the round/story log when the passage was rejected */
	rawReply?: string|n;
	/** why the model path failed, when it did */
	error?: string|n;
};

export type InterludeOptions = {
	world: EchoWorld;
	character: EchoCharacter;
	/** the storyline so far (§8.1) -- the same text the weaver receives */
	summary?: string|n;
	/** 0..1 at the moment of crossing (§8.4) */
	suspicion: number;
	/** the `narrator` role's model; "" lets the backend decide */
	model?: string|n;
	/** §7 gate options. A narration passage is new prose, so it gets the same house-style check the woven
	 * bullets get -- a `--` dash habit reappearing in the one passage the user reads most carefully would
	 * be worse than in a bullet. */
	checkOptions?: CheckBulletOptions|n;
	counter?: LLMCallCounter|n;
	/** total calls before falling back. Default 2 (one informed re-ask). */
	maxAttempts?: number;
};

/** Builds the prompt inputs from a context, exported so the test can assert what the prompt sees
 * (and, crucially, that there is no field through which a label could arrive). */
export function ToInterludePromptOptions(options: InterludeOptions, fixNotes?: string|n): Interlude_UserPromptOptions {
	return {
		worldName: options.world.name,
		worldBible: options.world.bible,
		worldCrazinessTolerance: options.world.crazinessTolerance,
		characterName: options.character.name,
		characterCautionVsOpportunity: options.character.values_cautionVsOpportunity,
		characterNotes: options.character.personalityNotes,
		summary: options.summary,
		suspicion: options.suspicion,
		fixNotes,
	};
}

/** Coerces a parsed reply into the passage text, or explains why it is not usable.
 *
 * Two salvages, both cheap: a bare string reply (a model that answered with prose instead of the object)
 * and an object under a near-miss key. Neither changes anything the user can observe, so rejecting them
 * would cost a retry for nothing -- the same posture as `ParseWeaveReply`'s `doorOffer` handling. */
export function ParseInterludeReply(parsed: unknown): {text: string|null, problems: string[]} {
	if (parsed == null) return {text: null, problems: ["No usable reply."]};

	if (typeof parsed == "string") {
		const text = parsed.trim();
		if (text.length > 0) return {text, problems: []};
		return {text: null, problems: ["The reply was an empty string."]};
	}

	if (typeof parsed != "object" || Array.isArray(parsed)) {
		return {text: null, problems: ["The reply was not a JSON object."]};
	}

	const obj = parsed as Record<string, unknown>;
	// `interlude` first, then the two near-misses a model actually produces
	for (const key of ["interlude", "passage", "text"]) {
		const value = obj[key];
		if (typeof value == "string" && value.trim().length > 0) {
			return {text: value.trim(), problems: []};
		}
	}
	return {text: null, problems: ["The reply has no non-empty `interlude` field."]};
}

/** Validates the passage: non-empty, and clean under the §7 gate.
 *
 * Deliberately NOT length-checked. A 90-word passage is a perfectly usable interlude, and the failure
 * mode of a length rule is a paid retry to fix arithmetic -- unlike the fake generator, where a short
 * bullet is a genuine deviation from the round's shape. */
export function ValidateInterlude(text: string|null, checkOptions?: CheckBulletOptions|n): {ok: boolean, problems: string[]} {
	if (text == null || text.trim().length == 0) {
		return {ok: false, problems: ["The passage is empty."]};
	}

	const violations = CheckBullet(text, checkOptions ?? {});
	if (violations.length > 0) {
		return {ok: false, problems: violations.map(a=>`The passage breaks the house style: ${a.message}`)};
	}

	return {ok: true, problems: []};
}

/** The passage written when the model path fails. It states the two facts the interlude exists to convey
 * -- the character checked something, and the experience ended -- without pretending to be prose the
 * model wrote. The story-end message on screen says the model failed, so this text is never mistaken for
 * a good passage. */
export function BuildFallbackInterlude(options: {characterName: string}): string {
	const name = options.characterName.trim().length > 0 ? options.characterName : "The character";
	return [
		`${name} stopped walking and looked at the thing in front of them for a second time.`,
		"It was exactly as it had been, and that was the problem: nothing in the last hour had stayed",
		"as it should, and they had been explaining each instance away as it came.",
		"They checked once more, deliberately, and got the same impossible answer.",
		`${name} understood, then, where they were -- and the experience ended there.`,
	].join(" ");
}

/** Writes the lucid interlude (§8.4). One informed re-ask on a rejection, then the fallback passage.
 *
 * The story ends either way; this only decides WHOSE words describe it. */
export async function WriteInterlude(backend: LLMBackend|n, options: InterludeOptions): Promise<InterludeResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 2);
	let outputTokens = 0;
	let callCount = 0;
	let fixNotes: string|n = null;
	let lastRaw: string|n = null;
	let lastError: string|n = null;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		if (backend == null) {
			lastError = "No LLM backend is configured.";
			break;
		}

		const result = await CallLLM(backend, {
			role: "narrator",
			model: options.model ?? null,
			system: Interlude_SystemPrompt(),
			user: Interlude_UserPrompt(ToInterludePromptOptions(options, fixNotes)),
			json: Interlude_JSONSchema(),
			counter: options.counter ?? null,
		});
		callCount++;
		outputTokens += result.outputTokens;
		lastRaw = result.text;

		if (result.error != null) {
			lastError = result.error;
			fixNotes = result.error;
			continue;
		}

		const parsed = ParseInterludeReply(result.parsed);
		const validation = ValidateInterlude(parsed.text, options.checkOptions);
		const problems = [...parsed.problems, ...validation.problems];

		if (problems.length == 0) {
			return {text: parsed.text!, generatedByModel: true, outputTokens, callCount, rawReply: lastRaw};
		}

		lastError = problems.join(" ");
		fixNotes = problems.join("\n");
	}

	// the meter crossing already happened, so the story still ends -- with words that say so plainly
	return {
		text: BuildFallbackInterlude({characterName: options.character.name}),
		generatedByModel: false,
		outputTokens,
		callCount,
		rawReply: lastRaw,
		error: lastError ?? "The interlude could not be written.",
	};
}
